import type { FeatureResponse } from '@monorepo/shared/schemas/features'
import { Activity, DollarSign, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Card, CardContent } from '@/shared/components/ui/card'
import { FeatureCostSignalsTable } from './FeatureCostSignalsTable'
import { FeatureDrawer } from './FeatureDrawer'
import { FeaturePreviewCard } from './FeaturePreviewCard'

interface FeatureDetailProps {
  feature?: FeatureResponse
  featureId?: string
  onSave?: (feature: FeatureResponse) => void
  onCancel?: () => void
}

export function FeatureDetail({
  feature: propFeature,
  featureId: _propFeatureId,
  onSave: _onSave,
  onCancel: _onCancel,
}: FeatureDetailProps) {
  const [currentFeature, setCurrentFeature] = useState<
    FeatureResponse | undefined
  >(propFeature)
  const [loading] = useState(false)
  const [showEditDrawer, setShowEditDrawer] = useState(false)

  // Update current feature when prop changes
  useEffect(() => {
    if (propFeature) {
      setCurrentFeature(propFeature)
    }
  }, [propFeature])

  // Use the most current feature data
  const feature = currentFeature || propFeature

  if (!feature) {
    return (
      <div className='max-w-7xl mx-auto p-6'>
        <div className='text-center py-12'>
          <p className='text-slate-600 dark:text-slate-400'>
            Feature not found
          </p>
        </div>
      </div>
    )
  }

  const hasBillableSignal = !!feature.billable_signal
  const costSignalsCount = feature.cost_signals?.length || 0

  return (
    <div className='max-w-7xl mx-auto p-6 space-y-6'>
      {/* Feature Preview Card at Top */}
      <FeaturePreviewCard
        feature={feature}
        isLoading={loading}
        onEdit={() => setShowEditDrawer(true)}
      />

      {/* Signal Details Section */}
      {hasBillableSignal && feature.billable_signal && (
        <div className='border border-gray-200/60 dark:border-gray-800/60 rounded-lg shadow-sm p-6 space-y-4'>
          <div className='flex items-center gap-2 pb-2'>
            <Zap className='w-5 h-5 text-blue-600' />
            <h3 className='text-lg font-semibold text-slate-900 dark:text-slate-100'>
              Billable Signal
            </h3>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <Label className='text-sm font-medium text-slate-600 dark:text-slate-400'>
                Signal Name
              </Label>
              <p className='text-base font-semibold text-slate-900 dark:text-slate-100'>
                {feature.billable_signal.signal_name}
              </p>
            </div>
            <div>
              <Label className='text-sm font-medium text-slate-600 dark:text-slate-400'>
                Signal Code
              </Label>
              <p className='text-base font-mono text-slate-900 dark:text-slate-100'>
                {feature.billable_signal.signal_code}
              </p>
            </div>
            <div>
              <Label className='text-sm font-medium text-slate-600 dark:text-slate-400'>
                Aggregation
              </Label>
              <Badge variant='outline'>
                {feature.billable_signal.aggregation_type}
              </Badge>
            </div>
            <div>
              <Label className='text-sm font-medium text-slate-600 dark:text-slate-400'>
                Unit Label
              </Label>
              <p className='text-base text-slate-900 dark:text-slate-100'>
                {feature.billable_signal.unit_label}
              </p>
            </div>
          </div>

          {feature.value && (
            <div className='mt-4 pt-4 border-t border-slate-200/60 dark:border-slate-800/60'>
              <Label className='text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 block'>
                Value Metrics
              </Label>
              <div className='grid grid-cols-2 gap-4'>
                {feature.value.revenue_per_unit != null && (
                  <div className='flex items-center gap-2'>
                    <DollarSign className='w-4 h-4 text-green-600' />
                    <div>
                      <p className='text-xs text-slate-500'>Revenue per Unit</p>
                      <p className='text-base font-semibold text-green-600'>
                        ${feature.value.revenue_per_unit.toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
                {feature.value.default_price_cents != null && (
                  <div className='flex items-center gap-2'>
                    <DollarSign className='w-4 h-4 text-blue-600' />
                    <div>
                      <p className='text-xs text-slate-500'>Default Price</p>
                      <p className='text-base font-semibold text-blue-600'>
                        ${(feature.value.default_price_cents / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cost Signals Section */}
      {costSignalsCount > 0 && feature.cost_signals && (
        <div className='space-y-4'>
          <div className='flex items-center gap-2'>
            <Activity className='w-5 h-5 text-orange-600' />
            <h3 className='text-lg font-semibold text-slate-900 dark:text-slate-100'>
              Cost Signals ({costSignalsCount})
            </h3>
          </div>
          <FeatureCostSignalsTable costSignals={feature.cost_signals} />
        </div>
      )}

      {/* No Signals State */}
      {!hasBillableSignal && costSignalsCount === 0 && (
        <Card>
          <CardContent className='py-12 text-center'>
            <Zap className='w-12 h-12 mx-auto text-slate-300 mb-4' />
            <h3 className='text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2'>
              No Signals Configured
            </h3>
            <p className='text-sm text-slate-600 dark:text-slate-400'>
              This feature doesn't have any signals configured yet. Add signals
              to start tracking usage and generating revenue.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Drawer */}
      <FeatureDrawer
        feature={feature}
        open={showEditDrawer}
        onOpenChange={setShowEditDrawer}
      />
    </div>
  )
}

// Label component helper
function Label({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <div className={className}>{children}</div>
}
